import axios, { type AxiosResponse } from "axios";
import {
  IMPACT_REPORT_PDF_CONTENT_TYPE,
  IMPACT_REPORT_PDF_CONVERSIONS_PATH,
  IMPACT_REPORT_PDF_EOF_MARKER,
  IMPACT_REPORT_PDF_HEADER_MARKER,
  IMPACT_REPORT_PDF_MARKER_WINDOW_BYTES,
} from "@/constants/impactReport";
import type { PdfConversionJob } from "@/interfaces/impactReportPdf";
import { apiClient } from "@/lib/apiClient";

/**
 * Platform's proxy over the queued PDF converter: enqueue, status, collect.
 *
 * The only module in the UI that calls these routes. Everything that orchestrates a download —
 * serialise, enqueue, poll, collect, save — lives one layer up in
 * `utils/impact-report-pdf-download-helper.ts`, this file's one caller. `apiClient`'s request
 * interceptor attaches the Clerk bearer token, so there is no auth handling here.
 */

/** Hands the serialised document to the converter's queue. Returns as soon as it is accepted. */
export async function enqueueImpactReportPdfConversion(
  html: string,
  filename: string | undefined,
  signal: AbortSignal,
): Promise<PdfConversionJob> {
  const { data } = await apiClient.post<PdfConversionJob>(
    IMPACT_REPORT_PDF_CONVERSIONS_PATH,
    filename === undefined ? { html } : { html, filename },
    { signal },
  );
  return data;
}

/** One job's current state — what the browser polls between enqueue and collect. */
export async function fetchImpactReportPdfConversionStatus(
  jobId: string,
  signal: AbortSignal,
): Promise<PdfConversionJob> {
  const { data } = await apiClient.get<PdfConversionJob>(
    `${IMPACT_REPORT_PDF_CONVERSIONS_PATH}/${encodeURIComponent(jobId)}`,
    { signal },
  );
  return data;
}

/**
 * Re-reads a `responseType: "blob"` ERROR body as the JSON envelope it actually is, then rethrows
 * the same `AxiosError`.
 *
 * Axios applies `responseType` to non-2xx responses too, so a failed collect leaves
 * `error.response.data` holding a `Blob` of Platform's `{ success: false, error: "<message>" }`
 * rather than the parsed object, and the whitelisted reason in it would never reach the user.
 */
async function decodeBlobErrorBody(error: unknown): Promise<never> {
  if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
    const text = await error.response.data.text().catch(() => "");
    try {
      error.response.data = text ? JSON.parse(text) : undefined;
    } catch {
      console.warn(
        "fetchImpactReportPdfConversionContent: non-JSON error body",
        error.response.status,
        text.slice(0, 200),
      );
      error.response.data = undefined;
    }
  }
  throw error;
}

/** The response's media type without parameters, lower-cased: `"application/pdf"`, or `""`. */
function mediaType(contentType: unknown): string {
  return typeof contentType === "string"
    ? (contentType.split(";")[0] ?? "").trim().toLowerCase()
    : "";
}

/** Whether `blob` opens and closes like a whole PDF: its header near the start, `%%EOF` near the end. */
async function isWholePdf(blob: Blob): Promise<boolean> {
  const [head, tail] = await Promise.all([
    blob.slice(0, IMPACT_REPORT_PDF_MARKER_WINDOW_BYTES).text(),
    blob.slice(-IMPACT_REPORT_PDF_MARKER_WINDOW_BYTES).text(),
  ]);
  return (
    head.includes(IMPACT_REPORT_PDF_HEADER_MARKER) && tail.includes(IMPACT_REPORT_PDF_EOF_MARKER)
  );
}

/**
 * The rendered bytes for a completed job.
 *
 * `suggestedFilename` is forwarded as `?filename=` for the server's own `Content-Disposition`; it
 * does not decide what the browser saves the file as (`lib/csv.ts#downloadBlob` does).
 *
 * A 2xx is only a report if it SAYS it is a PDF and its bytes ARE a whole one. Anything else — a
 * gateway's HTML page, a JSON body, a body cut off mid-stream — is refused here rather than saved
 * to disk under a `.pdf` name.
 */
export async function fetchImpactReportPdfConversionContent(
  jobId: string,
  suggestedFilename: string | undefined,
  signal: AbortSignal,
): Promise<Blob> {
  let response: AxiosResponse<Blob>;
  try {
    response = await apiClient.get<Blob>(
      `${IMPACT_REPORT_PDF_CONVERSIONS_PATH}/${encodeURIComponent(jobId)}/content`,
      {
        params: suggestedFilename ? { filename: suggestedFilename } : undefined,
        responseType: "blob",
        signal,
      },
    );
  } catch (error) {
    return await decodeBlobErrorBody(error);
  }

  const contentType = mediaType(response.headers["content-type"]);
  if (contentType !== IMPACT_REPORT_PDF_CONTENT_TYPE) {
    throw new Error(
      `fetchImpactReportPdfConversionContent: expected ${IMPACT_REPORT_PDF_CONTENT_TYPE}, received "${contentType || "(none)"}" (HTTP ${response.status})`,
    );
  }
  if (!(await isWholePdf(response.data))) {
    throw new Error(
      `fetchImpactReportPdfConversionContent: body is not a whole PDF (${response.data.size} bytes)`,
    );
  }
  return response.data;
}
